'use strict';

/**
 * Where a workspace's Back link goes: the list or dashboard URL its record was opened from, with that list's
 * filter, sort, search and paging still in the route, or the config's Header.ListRoute when the record was
 * opened some other way (a pasted link, a new tab, another workspace).
 */
class WorkspaceReturnRoutes
{
	constructor()
	{
		/** @type {Array<{ Config: any, DefaultListRoute: string, RecordPattern: RegExp, ListPattern: RegExp }>} */
		this.entries = [];
		/** @type {Object<string, string>} the remembered return route, by workspace Identifier */
		this.returnRoutes = {};
		this.installed = false;
	}

	/**
	 * Track a workspace config and point its Header.ListRoute at the remembered return route.
	 *
	 * @param {any} pConfig - a pict-section-workspace config
	 */
	applyTo(pConfig)
	{
		const tmpHeader = pConfig && pConfig.Header;
		if (!tmpHeader || (typeof tmpHeader.ListRoute !== 'string') || !pConfig.Entity || !pConfig.Entity.Name)
		{
			return;
		}
		const tmpEntity = escapeRegExp(pConfig.Entity.Name);
		const tmpPrefix = escapeRegExp(pConfig.RoutePrefix || pConfig.Entity.Name);
		const tmpEntry =
		{
			Config: pConfig,
			DefaultListRoute: tmpHeader.ListRoute,
			RecordPattern: new RegExp(`^#/(PSRS/${tmpEntity}/(View|Edit|Create)(/|$)|${tmpPrefix}/Workspace/)`),
			ListPattern: new RegExp(`^#/(PSRS/${tmpEntity}/List(/|$)|Dashboards(/|$))`)
		};
		this.entries.push(tmpEntry);
		Object.defineProperty(tmpHeader, 'ListRoute',
			{
				configurable: true,
				enumerable: true,
				get: () => this.listRouteFor(pConfig.Identifier, tmpEntry.DefaultListRoute)
			});
	}

	/**
	 * @param {string} pIdentifier - the workspace Identifier
	 * @param {string} pDefaultListRoute
	 * @return {string} the remembered return route, else the default
	 */
	listRouteFor(pIdentifier, pDefaultListRoute)
	{
		return this.returnRoutes[pIdentifier] || pDefaultListRoute;
	}

	/**
	 * Remember the list a record was opened from: a move from a workspace's own list (or a dashboard) into one of
	 * its record routes. Moves between record routes (tabs, edit, save) keep what was remembered.
	 *
	 * @param {string} pFromHash - e.g. '#/PSRS/Sample/List/FilteredTo/FSF~CreateDate~DESC~0'
	 * @param {string} pToHash - e.g. '#/PSRS/Sample/View/<GUID>'
	 */
	noteRouteChange(pFromHash, pToHash)
	{
		for (const tmpEntry of this.entries)
		{
			if (tmpEntry.RecordPattern.test(pToHash) && tmpEntry.ListPattern.test(pFromHash))
			{
				this.returnRoutes[tmpEntry.Config.Identifier] = pFromHash;
			}
		}
	}

	/**
	 * Listen for route changes. Link clicks (a list row, a Sample ID link) and `location.hash` assignments fire
	 * hashchange, whose oldURL is the list's URL as shown, including routes the router pushed without one.
	 *
	 * @param {any} pWindow
	 */
	install(pWindow)
	{
		if (this.installed || !pWindow || (typeof pWindow.addEventListener !== 'function'))
		{
			return;
		}
		this.installed = true;
		pWindow.addEventListener('hashchange', (pEvent) =>
		{
			this.noteRouteChange(hashOf(pEvent && pEvent.oldURL), hashOf(pEvent && pEvent.newURL));
		});
	}
}

/**
 * @param {string} pURL
 * @return {string} the '#…' part of a URL, or ''
 */
function hashOf(pURL)
{
	if (typeof pURL !== 'string')
	{
		return '';
	}
	const tmpIndex = pURL.indexOf('#');
	return (tmpIndex < 0) ? '' : pURL.slice(tmpIndex);
}

/**
 * @param {string} pValue
 * @return {string}
 */
function escapeRegExp(pValue)
{
	return String(pValue).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

module.exports = WorkspaceReturnRoutes;
module.exports.hashOf = hashOf;
